import { SimpleDataTable } from "@octopusdeploy/design-system-components";
import type { DeploymentTargetResource, ResourceCollection, TaskResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { DeploymentTargetDeploymentsGrouping, DeploymentTargetDeploymentsGroupingMenu } from "~/areas/infrastructure/components/DeploymentTargets/DeploymentTargetDeploymentsGroupingMenu";
import { ProjectsDashboardSearchBox } from "~/areas/projects/components/Projects/ProjectsDashboardSearchBox";
import { repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import FormPage from "~/components/FormPage/FormPage";
import Logo from "~/components/Logo/index";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import List from "~/components/PagingList";
import TaskDetails from "~/components/TaskDetails/TaskDetails";
import type { LatestDeploymentsByProject } from "../../../../../../../packages/octopus-server-client/src/resources/latestDeploymentsByProject";
import styles from "./style.module.less";
class DeploymentTasksList extends List<TaskResource<{}>> {
}
interface DeploymentTargetDeploymentsPageProps {
    machineId: string;
}
interface DeploymentTargetDeploymentsPageInternalProps {
    initialData: InitialData;
}
interface InitialData {
    machine: DeploymentTargetResource;
    tasksResponse: ResourceCollection<TaskResource<{}>>;
    tasksByProjectResponse: ResourceCollection<LatestDeploymentsByProject> | null;
}
interface MachineDeploymentsState extends DataBaseComponentState {
    tasksResponse: ResourceCollection<TaskResource<{}>>;
    tasksByProjectResponse: ResourceCollection<LatestDeploymentsByProject> | null;
    groupBy: DeploymentTargetDeploymentsGrouping;
    partialName: string | undefined;
}
const Title = "Deployments";
const DeploymentTargetDeploymentsLayoutFormPage = FormPage<InitialData>();
export function DeploymentTargetDeploymentsPage({ machineId }: DeploymentTargetDeploymentsPageProps) {
    return (<DeploymentTargetDeploymentsLayoutFormPage title={Title} load={async () => {
            const machine = await repository.Machines.get(machineId);
            const tasksResponse = await repository.Machines.getDeployments(machine, { skip: 0 });
            const tasksByProjectResponse = isFeatureToggleEnabled("GetLatestDeploymentPerProjectForDeploymentTargetFeatureToggle") ? await repository.Machines.getLatestDeploymentPerProject(machine.Id, { skip: 0 }) : null;
            return { machine, tasksResponse, tasksByProjectResponse };
        }} renderWhenLoaded={(data) => <DeploymentTargetDeploymentsLayoutInner initialData={data}/>}/>);
}
class DeploymentTargetDeploymentsLayoutInner extends DataBaseComponent<DeploymentTargetDeploymentsPageInternalProps, MachineDeploymentsState> {
    constructor(props: DeploymentTargetDeploymentsPageInternalProps) {
        super(props);
        this.state = {
            tasksResponse: props.initialData.tasksResponse,
            groupBy: DeploymentTargetDeploymentsGrouping.None,
            tasksByProjectResponse: props.initialData.tasksByProjectResponse,
            partialName: undefined,
        };
    }
    render() {
        return (<PageContent header={{ title: Title }} busy={this.state.busy} errors={this.errors}>
                {isFeatureToggleEnabled("GetLatestDeploymentPerProjectForDeploymentTargetFeatureToggle") && (<div className={styles.projectFilterContainer}>
                        <DeploymentTargetDeploymentsGroupingMenu grouping={this.state.groupBy} onChange={(group) => this.updateGrouping(group)}/>
                        {this.state.groupBy === DeploymentTargetDeploymentsGrouping.ByProject && (<div className={styles.searchInput}>
                                <ProjectsDashboardSearchBox searchValue={this.state.partialName} onSearchValueChange={(value) => this.handleProjectNameFilterChange(value)} placeholder={"Search by project name"} debounceValue={300}/>
                            </div>)}
                    </div>)}
                {this.state.groupBy === DeploymentTargetDeploymentsGrouping.ByProject && this.state.tasksByProjectResponse !== null ? (<SimpleDataTable accessibleName={"Latest deployments per project"} data={this.state.tasksByProjectResponse.Items} columns={[
                    {
                        render: (item) => {
                            return (<div className={styles.projectCell}>
                                            <div>
                                                <Logo url={item.ProjectLogo} size={"2.2rem"}/>
                                            </div>
                                            <div>
                                                <InternalLink to={links.projectRootRedirect.generateUrl({ spaceId: this.props.initialData.machine.SpaceId, projectSlug: item.ProjectId })}>
                                                    <span className={styles.projectName}>
                                                        <strong>{item.ProjectName}</strong>
                                                    </span>
                                                </InternalLink>
                                            </div>
                                        </div>);
                        },
                        title: "Project",
                        columnSize: "medium",
                        accessibleName: "Project",
                    },
                    {
                        render: (item) => {
                            return (<InternalLink to={links.taskPage.generateUrl({ taskId: item.ServerTask.Id })}>
                                            <div className={styles.taskDetails}>
                                                <TaskDetails task={item.ServerTask} stripTopBottomPadding={true}/>
                                            </div>
                                        </InternalLink>);
                        },
                        title: "Latest Deployment",
                        accessibleName: "Latest Deployment",
                    },
                ]} getRowKey={(item) => item.ServerTask.Id}/>) : (<DeploymentTasksList initialData={this.state.tasksResponse} onRow={(item: TaskResource<{}>) => this.buildDeploymentTaskRow(item)} onRowRedirectUrl={(task: TaskResource<{}>) => links.taskPage.generateUrl({ taskId: task.Id })} showPagingInNumberedStyle={true} onPageSelected={async (skip: number, p: number) => {
                    await this.loadDeploymentTasks(skip);
                }}/>)}
            </PageContent>);
    }
    private async loadDeploymentTasks(skip: number) {
        const tasksResponse = await repository.Machines.getDeployments(this.props.initialData.machine, { skip });
        this.setState({ tasksResponse });
    }
    private buildDeploymentTaskRow(task: TaskResource<{}>) {
        return <TaskDetails task={task} stripTopBottomPadding={true}/>;
    }
    private async loadDeploymentsByProject(skip: number, partialName?: string) {
        const tasksByProject = await repository.Machines.getLatestDeploymentPerProject(this.props.initialData.machine.Id, { skip, partialName });
        this.setState({ tasksByProjectResponse: tasksByProject });
    }
    private buildDeploymentByProjectRow(item: LatestDeploymentsByProject) {
        const taskDetails = <TaskDetails task={item.ServerTask} stripTopBottomPadding={true}/>;
        return [item.ProjectName, taskDetails];
    }
    private async updateGrouping(group: DeploymentTargetDeploymentsGrouping) {
        this.setState({ groupBy: group });
        switch (group) {
            case DeploymentTargetDeploymentsGrouping.None:
                await this.loadDeploymentTasks(0);
                break;
            case DeploymentTargetDeploymentsGrouping.ByProject:
                await this.loadDeploymentsByProject(0);
                break;
        }
    }
    private async handleProjectNameFilterChange(partialName: string) {
        this.setState({ partialName });
        await this.loadDeploymentsByProject(0, partialName);
    }
    static displayName = "DeploymentTargetDeploymentsLayoutInner";
}
